// Badge and Level System for TCM Study Club

// Calculate level and progress based on post count
export const calculateLevel = (postCount) => {
  let level = 0;
  let totalNeeded = 0;
  let postsForNextLevel = 15;

  // Level progression with increasing requirements to reach Level 100 at 10,000 posts
  // Grand Master tier starts at Level 101 (20,000 posts total)
  const levelRanges = [
    { max: 10, postsPerLevel: 15 },    // Levels 1-10: 15 posts each = 150 total
    { max: 20, postsPerLevel: 25 },    // Levels 11-20: 25 posts each = 400 total
    { max: 30, postsPerLevel: 40 },    // Levels 21-30: 40 posts each = 800 total
    { max: 40, postsPerLevel: 60 },    // Levels 31-40: 60 posts each = 1,400 total
    { max: 50, postsPerLevel: 80 },    // Levels 41-50: 80 posts each = 2,200 total
    { max: 60, postsPerLevel: 110 },   // Levels 51-60: 110 posts each = 3,300 total
    { max: 70, postsPerLevel: 150 },   // Levels 61-70: 150 posts each = 4,800 total
    { max: 80, postsPerLevel: 200 },   // Levels 71-80: 200 posts each = 6,800 total
    { max: 90, postsPerLevel: 260 },   // Levels 81-90: 260 posts each = 9,400 total
    { max: 100, postsPerLevel: 60 },   // Levels 91-100: 60 posts each = 10,000 total
    { max: 120, postsPerLevel: 500 },  // Levels 101-120: 500 posts each = 20,000 total (GRAND MASTER)
  ];

  for (const range of levelRanges) {
    const levelsInRange = range.max - level;
    const postsNeededForRange = levelsInRange * range.postsPerLevel;

    if (postCount >= totalNeeded + postsNeededForRange) {
      totalNeeded += postsNeededForRange;
      level = range.max;
      postsForNextLevel = range.postsPerLevel;
    } else {
      const postsIntoRange = postCount - totalNeeded;
      level += Math.floor(postsIntoRange / range.postsPerLevel);
      const progressInLevel = postsIntoRange % range.postsPerLevel;
      postsForNextLevel = range.postsPerLevel;
      
      return {
        level: Math.min(level, 120),
        progress: progressInLevel,
        postsForNextLevel: level >= 120 ? 0 : postsForNextLevel,
        progressPercent: level >= 120 ? 100 : (progressInLevel / postsForNextLevel) * 100,
        tier: getBadgeTier(Math.min(level, 120)),
        badgeInfo: getBadgeInfo(Math.min(level, 120))
      };
    }
  }

  // Max level reached
  return {
    level: 120,
    progress: 0,
    postsForNextLevel: 0,
    progressPercent: 100,
    tier: getBadgeTier(120),
    badgeInfo: getBadgeInfo(120)
  };
};

// Get badge tier based on level
export const getBadgeTier = (level) => {
  if (level >= 101) return 11; // Immortal tier (ultimate)
  if (level >= 91) return 10;  // Grand Master tier
  if (level >= 81) return 9;
  if (level >= 71) return 8;
  if (level >= 61) return 7;
  if (level >= 51) return 6;
  if (level >= 41) return 5;
  if (level >= 31) return 4;
  if (level >= 21) return 3;
  if (level >= 11) return 2;
  return 1;
};

// Get badge information with colors and names
export const getBadgeInfo = (level) => {
  const tier = getBadgeTier(level);
  
  const badges = {
    1: {
      name: 'Novice Scholar',
      color: 'from-orange-400 to-amber-600',
      icon: '🌱',
      description: 'Beginning your TCM journey',
      textColor: 'text-amber-700',
      borderColor: 'border-amber-400',
      bgColor: 'bg-amber-50'
    },
    2: {
      name: 'Apprentice',
      color: 'from-gray-400 to-gray-600',
      icon: '📚',
      description: 'Developing clinical insight',
      textColor: 'text-gray-700',
      borderColor: 'border-gray-400',
      bgColor: 'bg-gray-50'
    },
    3: {
      name: 'Practitioner',
      color: 'from-yellow-400 to-yellow-600',
      icon: '⚡',
      description: 'Demonstrating expertise',
      textColor: 'text-yellow-700',
      borderColor: 'border-yellow-400',
      bgColor: 'bg-yellow-50'
    },
    4: {
      name: 'Expert',
      color: 'from-slate-300 to-slate-500',
      icon: '💎',
      description: 'Advanced knowledge',
      textColor: 'text-slate-700',
      borderColor: 'border-slate-400',
      bgColor: 'bg-slate-50'
    },
    5: {
      name: 'Master',
      color: 'from-cyan-400 to-blue-600',
      icon: '💠',
      description: 'Masterful understanding',
      textColor: 'text-blue-700',
      borderColor: 'border-blue-400',
      bgColor: 'bg-blue-50'
    },
    6: {
      name: 'Grandmaster',
      color: 'from-blue-500 to-indigo-700',
      icon: '🔷',
      description: 'Elite clinical reasoning',
      textColor: 'text-indigo-700',
      borderColor: 'border-indigo-400',
      bgColor: 'bg-indigo-50'
    },
    7: {
      name: 'Legend',
      color: 'from-red-500 to-rose-700',
      icon: '🔴',
      description: 'Legendary contributor',
      textColor: 'text-rose-700',
      borderColor: 'border-rose-400',
      bgColor: 'bg-rose-50'
    },
    8: {
      name: 'Sage',
      color: 'from-emerald-500 to-green-700',
      icon: '🟢',
      description: 'Sage wisdom',
      textColor: 'text-green-700',
      borderColor: 'border-green-400',
      bgColor: 'bg-green-50'
    },
    9: {
      name: 'Oracle',
      color: 'from-purple-500 to-violet-700',
      icon: '🔮',
      description: 'Oracle of TCM',
      textColor: 'text-purple-700',
      borderColor: 'border-purple-400',
      bgColor: 'bg-purple-50'
    },
    10: {
      name: 'Grand Master',
      color: 'from-yellow-400 via-orange-500 to-red-600',
      icon: '👑',
      description: 'Supreme Grand Master',
      textColor: 'text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-red-600',
      borderColor: 'border-yellow-400',
      bgColor: 'bg-gradient-to-r from-yellow-50 via-orange-50 to-red-50'
    },
    11: {
      name: 'Immortal',
      color: 'from-pink-500 via-purple-500 to-indigo-500',
      icon: '✨',
      description: 'Immortal TCM Master - Ultimate Achievement',
      textColor: 'text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-500',
      borderColor: 'border-pink-400',
      bgColor: 'bg-gradient-to-r from-pink-50 via-purple-50 to-indigo-50',
      special: true
    }
  };

  return badges[tier];
};

// Get total posts needed to reach a specific level
export const getPostsForLevel = (targetLevel) => {
  let totalPosts = 0;
  let currentLevel = 0;

  const levelRanges = [
    { max: 10, postsPerLevel: 15 },
    { max: 20, postsPerLevel: 25 },
    { max: 30, postsPerLevel: 40 },
    { max: 40, postsPerLevel: 60 },
    { max: 50, postsPerLevel: 80 },
    { max: 60, postsPerLevel: 110 },
    { max: 70, postsPerLevel: 150 },
    { max: 80, postsPerLevel: 200 },
    { max: 90, postsPerLevel: 260 },
    { max: 100, postsPerLevel: 60 },
    { max: 120, postsPerLevel: 500 },
  ];

  for (const range of levelRanges) {
    if (targetLevel <= range.max) {
      totalPosts += (targetLevel - currentLevel) * range.postsPerLevel;
      break;
    } else {
      totalPosts += (range.max - currentLevel) * range.postsPerLevel;
      currentLevel = range.max;
    }
  }

  return totalPosts;
};

